import React, { useMemo } from 'react'
import { Box, Typography, styled, LinearProgress, Button, Grid } from '@mui/material'
import { CloudUpload, Pattern, PhotoLibrary, ArrowForward } from '@mui/icons-material'
import useAppStore from '@/store/useAppStore'
import { shallow } from 'zustand/shallow'
import { useNavigate } from 'react-router-dom'
import { TopicType, TopicTypes } from '@/utils/constants'

// ==================== STYLED COMPONENTS ====================
const SectionHeader = styled(Box)({
  marginBottom: '32px',
})

const SectionTitle = styled(Typography)({
  fontSize: '28px',
  fontWeight: 700,
  color: '#111827',
  letterSpacing: '-0.02em',
  marginBottom: '8px',
})

const SectionDescription = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#6b7280',
  lineHeight: 1.6,
})

const Card = styled(Box)({
  backgroundColor: '#ffffff',
  border: '1px solid #e5e7eb',
  borderRadius: '12px',
  marginBottom: '16px',
})

const CardHeader = styled(Box)({
  padding: '20px 24px',
  borderBottom: '1px solid #e5e7eb',
})

const CardContent = styled(Box)({
  padding: '24px',
})

const StatCard = styled(Box)(({ theme }) => ({
  padding: '20px',
  borderRadius: '10px',
  border: '1px solid #e5e7eb',
  backgroundColor: '#ffffff',
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  transition: 'all 0.2s ease',
  cursor: 'pointer',

  '&:hover': {
    borderColor: '#3b82f6',
    boxShadow: '0 2px 8px rgba(59, 130, 246, 0.1)',
    transform: 'translateY(-2px)',
  },
}))

const IconWrapper = styled(Box)<{ color: string }>(({ color }) => ({
  width: 48,
  height: 48,
  borderRadius: '10px',
  backgroundColor: `${color}15`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  '& .MuiSvgIcon-root': {
    fontSize: '24px',
    color,
  },
}))

// ==================== COMPONENT ====================
const ContentSection: React.FC = () => {
  const navigate = useNavigate()
  const [topicList] = useAppStore((state) => [state.home.topicList], shallow)

  const stats = useMemo(() => {
    const allTopics = Object.values(topicList)
    const patterns = allTopics.filter((t) => t.topicType === TopicType.PATTERN.toString())
    const albums = allTopics.filter((t) => t.topicType === TopicType.ALBUM.toString())

    // Calculate storage (mock for now)
    const totalStorage = 5000 // MB
    const usedStorage = allTopics.length * 15 // Rough estimate

    return {
      patterns: patterns.length,
      albums: albums.length,
      total: allTopics.length,
      usedStorage,
      totalStorage,
      storagePercentage: (usedStorage / totalStorage) * 100,
    }
  }, [topicList])

  return (
    <>
      <SectionHeader>
        <SectionTitle>Content</SectionTitle>
        <SectionDescription>Manage your patterns, collections, and storage</SectionDescription>
      </SectionHeader>

      {/* Storage Usage */}
      <Card>
        <CardHeader>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: 600,
                color: '#111827',
              }}
            >
              Storage Usage
            </Typography>
            <Typography
              sx={{
                fontSize: '13px',
                fontWeight: 600,
                color: '#6b7280',
              }}
            >
              {stats.usedStorage}MB / {stats.totalStorage}MB
            </Typography>
          </Box>
        </CardHeader>
        <CardContent>
          <LinearProgress
            variant='determinate'
            value={stats.storagePercentage}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: '#e5e7eb',
              '& .MuiLinearProgress-bar': {
                borderRadius: 4,
                background:
                  stats.storagePercentage > 80
                    ? 'linear-gradient(90deg, #f59e0b 0%, #ef4444 100%)'
                    : 'linear-gradient(90deg, #3b82f6 0%, #8b5cf6 100%)',
              },
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
            <Typography
              sx={{
                fontSize: '13px',
                fontWeight: 500,
                color: '#6b7280',
              }}
            >
              {stats.total} total creations
            </Typography>
            {stats.storagePercentage > 80 && (
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#ef4444',
                }}
              >
                Running low on storage
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Content Stats */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <StatCard onClick={() => navigate('/workspace/pattern')}>
            <IconWrapper color='#8b5cf6'>
              <Pattern />
            </IconWrapper>
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  fontSize: '28px',
                  fontWeight: 700,
                  color: '#111827',
                  lineHeight: 1,
                  mb: 0.5,
                }}
              >
                {stats.patterns}
              </Typography>
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#6b7280',
                }}
              >
                Patterns
              </Typography>
            </Box>
            <ArrowForward sx={{ color: '#9ca3af' }} />
          </StatCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <StatCard onClick={() => navigate('/workspace/album')}>
            <IconWrapper color='#ec4899'>
              <PhotoLibrary />
            </IconWrapper>
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  fontSize: '28px',
                  fontWeight: 700,
                  color: '#111827',
                  lineHeight: 1,
                  mb: 0.5,
                }}
              >
                {stats.albums}
              </Typography>
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#6b7280',
                }}
              >
                {`${TopicTypes[TopicType.ALBUM].tabTitle}`}
              </Typography>
            </Box>
            <ArrowForward sx={{ color: '#9ca3af' }} />
          </StatCard>
        </Grid>
      </Grid>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: 600,
              color: '#111827',
            }}
          >
            Quick Actions
          </Typography>
        </CardHeader>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant='contained'
              startIcon={<Pattern />}
              onClick={() => navigate('/pattern')}
              sx={{
                fontSize: '14px',
                fontWeight: 600,
                textTransform: 'none',
                backgroundColor: '#8b5cf6',
                '&:hover': {
                  backgroundColor: '#7c3aed',
                },
              }}
            >
              Create Pattern
            </Button>
            <Button
              variant='contained'
              startIcon={<PhotoLibrary />}
              onClick={() => navigate('/topic/create')}
              sx={{
                fontSize: '14px',
                fontWeight: 600,
                textTransform: 'none',
                backgroundColor: '#ec4899',
                '&:hover': {
                  backgroundColor: '#db2777',
                },
              }}
            >
              {`Create ${TopicTypes[TopicType.ALBUM].title}`}
            </Button>
            <Button
              variant='outlined'
              startIcon={<CloudUpload />}
              onClick={() => navigate('/uploads')}
              sx={{
                fontSize: '14px',
                fontWeight: 600,
                textTransform: 'none',
                borderColor: '#d1d5db',
                color: '#4b5563',
                '&:hover': {
                  borderColor: '#9ca3af',
                  backgroundColor: '#f9fafb',
                },
              }}
            >
              Manage Uploads
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Storage Tip */}
      <Box
        sx={{
          p: 3,
          borderRadius: '10px',
          backgroundColor: '#eff6ff',
          border: '1px solid #dbeafe',
        }}
      >
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: 600,
            color: '#1e40af',
            mb: 0.5,
          }}
        >
          💡 Pro Tip
        </Typography>
        <Typography
          sx={{
            fontSize: '13px',
            fontWeight: 500,
            color: '#3b82f6',
            lineHeight: 1.6,
          }}
        >
          Upgrade to Pro for unlimited storage and advanced features. Your creations deserve the
          best!
        </Typography>
      </Box>
    </>
  )
}

export default ContentSection
